import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import { confirm } from '../../components/ConfirmDialog';

export default function KassaOfisPage() {
    const navigate = useNavigate();

    const [loading,    setLoading]    = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [summary,    setSummary]    = useState(null);
    const [amount,     setAmount]     = useState('');
    const [recent,     setRecent]     = useState([]);
    const [refundingId, setRefundingId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const [sRes, rRes] = await Promise.all([
                api.get('/kassa-ledger/ofis-summary'),
                api.get('/kassa-ledger/ofis-transfers-recent').catch(() => ({ data: { data: [] } })),
            ]);
            setSummary(sRes.data);
            setRecent(rRes.data?.data || []);
        } catch {
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const handleRefund = async (t) => {
        if (!(await confirm(`${Number(t.amount).toFixed(2)} ₼ köçürməni refund edəsən? Kassa artacaq, ofis azalacaq.`))) return;
        setRefundingId(t.id);
        try {
            const res = await api.post(`/kassa-ledger/ofis-transfer/${t.id}/refund`);
            toast.success(res.data?.msg || 'Refund edildi.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Refund alınmadı.');
        } finally {
            setRefundingId(null);
        }
    };

    useEffect(() => { load(); }, []);

    const handleTransfer = async (e) => {
        e.preventDefault();
        const amt = Number(amount);
        if (!amt || amt <= 0) return toast.error('Məbləğ daxil edin.');
        const kassaBalance = Number(summary?.kassa_balance || 0);
        if (amt > kassaBalance) return toast.error(`Kassada cəmi ${kassaBalance.toFixed(2)} ₼ var.`);
        setSubmitting(true);
        try {
            await api.post('/kassa-ledger/ofis-transfer', { amount: amt });
            toast.success(`${amt.toFixed(2)} ₼ Ofisə köçürüldü.`);
            setAmount('');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setSubmitting(false);
        }
    };

    const kassaBalance = Number(summary?.kassa_balance || 0);
    const overLimit    = !!amount && Number(amount) > kassaBalance;

    return (
        <Layout>
            <div className="min-h-screen bg-slate-50 p-4 md:p-8">

                {/* Header */}
                <div className="mb-6 flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50 transition-all"
                    >
                        ← Geri
                    </button>
                    <div>
                        <h1 className="text-xl font-black text-slate-800">🏢 Kassa → Ofis Köçürməsi</h1>
                        <p className="text-sm text-slate-400">Kassadan ofis seyfına nağd pul köçürməsi</p>
                    </div>
                </div>

                <div className="mx-auto max-w-md space-y-4">

                    {loading ? (
                        <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-400 text-sm">
                            Yüklənir...
                        </div>
                    ) : (
                        <>
                            {/* Stats */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-2xl bg-blue-50 border border-blue-100 p-4 text-center">
                                    <p className="text-[11px] font-bold text-blue-500 uppercase tracking-wide mb-1">Kassada var</p>
                                    <p className="text-3xl font-black text-blue-700">
                                        {summary ? kassaBalance.toFixed(2) : '—'}
                                    </p>
                                    <p className="text-sm font-semibold text-blue-500 mt-0.5">₼</p>
                                </div>
                                <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4 text-center">
                                    <p className="text-[11px] font-bold text-amber-500 uppercase tracking-wide mb-1">Bu gün göndərildi</p>
                                    <p className="text-3xl font-black text-amber-700">
                                        {summary ? Number(summary.ofis2_today).toFixed(2) : '—'}
                                    </p>
                                    <p className="text-sm font-semibold text-amber-500 mt-0.5">₼</p>
                                </div>
                            </div>

                            {/* Ofis balance strip */}
                            <div className="rounded-2xl bg-white border border-slate-200 px-5 py-4 flex items-center justify-between">
                                <span className="text-sm font-semibold text-slate-500">Ofis qalığı:</span>
                                <span className="text-xl font-black text-slate-800">
                                    {summary ? Number(summary.ofis2_balance).toFixed(2) : '—'} ₼
                                </span>
                            </div>

                            {/* Transfer form */}
                            <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-4">
                                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Köçürmə</h2>
                                <form onSubmit={handleTransfer} className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                                            Göndəriləcək məbləğ (₼)
                                        </label>
                                        <input
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            value={amount}
                                            onChange={e => setAmount(e.target.value)}
                                            placeholder="0.00"
                                            autoFocus
                                            className={`w-full border-2 rounded-xl px-4 py-4 text-center text-3xl font-black focus:outline-none transition-all ${
                                                overLimit
                                                    ? 'border-red-400 bg-red-50 text-red-600 focus:border-red-500'
                                                    : 'border-slate-200 focus:border-blue-400'
                                            }`}
                                        />
                                        {overLimit && (
                                            <p className="text-xs text-red-500 mt-1.5 text-center">
                                                Kassada maksimum {kassaBalance.toFixed(2)} ₼ köçürə bilərsiniz
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={submitting || !amount || Number(amount) <= 0 || overLimit}
                                        className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3.5 text-sm font-bold transition-all"
                                    >
                                        {submitting ? 'Göndərilir...' : '→ Ofisə köçür'}
                                    </button>
                                </form>
                            </div>

                            {/* Son 10 günün köçürmələri — refund */}
                            <div className="rounded-2xl bg-white border border-slate-200 p-5">
                                <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3">
                                    Son 10 gün — köçürmələr
                                </h2>
                                {recent.length === 0 ? (
                                    <p className="text-center text-sm text-slate-400 py-4">Köçürmə yoxdur</p>
                                ) : (
                                    <div className="space-y-2">
                                        {recent.map(t => (
                                            <div key={t.id} className="flex items-center gap-3 rounded-xl border border-slate-100 px-3 py-2.5">
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-bold text-slate-800">{Number(t.amount).toFixed(2)} ₼</div>
                                                    <div className="text-[11px] text-slate-400 truncate">
                                                        {t.date}{t.note ? ` · ${t.note}` : ''}
                                                    </div>
                                                </div>
                                                {Number(t.refunded) === 1 ? (
                                                    <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-400">Refund edilib</span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleRefund(t)}
                                                        disabled={refundingId === t.id}
                                                        className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-100 disabled:opacity-50"
                                                    >
                                                        {refundingId === t.id ? '...' : '↩ Refund'}
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
}
